"use client"

import { useEffect, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import {
Search,
Music2,
Heart,
Eye,
} from "lucide-react"

type playlistCreditosProps = {
data: {
playlistImages?: string[]
artistImgUrl: string
artistBio: string
artist?: string
artistHandle?: string
instagramUrl?: string
twitterUrl?: string
youtubeUrl?: string
spotifyUrl?: string
}
}

export default function playlistCreditos({
data,
}: playlistCreditosProps) {
const images = data.playlistImages ?? []
const [current, setCurrent] = useState(0)

useEffect(() => {
if (images.length <= 1) return


const interval = window.setInterval(() => {
  setCurrent((prev) => (prev + 1) % images.length)
}, 10000)

return () => window.clearInterval(interval)


}, [images.length])

return ( <footer
   className="
     w-full
     px-4
     pb-6
     pt-8
     landscape:px-4
     landscape:pb-4
     landscape:pt-0
     [display-mode:standalone]:pb-32
   "
 > <div
     className="
       mx-auto
       w-full
       //max-w-5xl
     "
   >
{/* =====================================================
ARTIST PANEL
====================================================== */}


    <div
      className="
        overflow-hidden
        rounded-[28px]
        bg-white/[0.1]
        ring-1
        ring-white/[0.08]
        landscape:grid
        landscape:grid-cols-[minmax(260px,400px)_1fr]
      "
    >
      {/* ===================================================
          MEDIA
      ==================================================== */}

      {images.length > 0 && (
        <div
          className="
            relative
            aspect-[4/3]
            w-full
            overflow-hidden
            landscape:aspect-auto
            landscape:min-h-[390px]
          "
        >
          <AnimatePresence mode="wait">
            <motion.img
              key={current}
              src={images[current]}
              alt=""
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 1.1,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="
                absolute
                inset-0
                h-full
                w-full
                object-cover
              "
            />
          </AnimatePresence>

          {/* subtle media overlay */}
          <div
            className="
              pointer-events-none
              absolute
              inset-0
              bg-gradient-to-t
              from-black/25
              via-transparent
              to-transparent
            "
          />

          {images.length > 1 && (
            <div
              className="
                absolute
                bottom-4
                left-4
                flex
                items-center
                gap-1.5
              "
            >
              {images.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  aria-label={`Mostrar imagen ${index + 1}`}
                  onClick={() => setCurrent(index)}
                  className="
                    h-1
                    rounded-full
                    bg-white
                    transition-all
                    duration-500
                  "
                  style={{
                    width: index === current ? 24 : 6,
                    opacity: index === current ? 0.95 : 0.35,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===================================================
          ARTIST INFORMATION
      ==================================================== */}

      <div
        className="
          flex
          min-w-0
          flex-col
          justify-between
          p-5
          landscape:min-h-[390px]
          landscape:p-7
        "
      >
        <div>
          {/* ARTIST IDENTITY */}

          <div
            className="
              flex
              items-center
              gap-3
            "
          >
            <img
              src={data.artistImgUrl}
              alt={data.artist ?? "Artista"}
              className="
                h-11
                w-11
                shrink-0
                rounded-full
                object-cover
              "
            />

            <div className="min-w-0">
              <p
                className="
                  text-[10px]
                  font-medium
                  uppercase
                  tracking-[0.18em]
                  text-white/40
                "
              >
                Sobre
              </p>

              <p
                className="
                  truncate
                  text-[15px]
                  font-medium
                  leading-tight
                  text-white
                "
              >
                {data.artist ?? "Artista"}
              </p>

              {data.artistHandle && (
                <p
                  className="
                    mt-0.5
                    truncate
                    text-xs
                    text-white/35
                  "
                >
                  {data.artistHandle}
                </p>
              )}
            </div>
          </div>

          {/* DESCRIPTION */}

          {/* {data.artistBio && (
            <p
              className="
                mt-7
                max-w-xl
                text-[clamp(1.35rem,3vw,2.15rem)]
                font-medium
                leading-[1.08]
                tracking-[-0.035em]
                text-white text-balance
                landscape:mt-8
              "
            >
              {data.artistBio}
            </p>
          )} */}

          {/* BIO */}

          {data.artistBio && (
            <a
              href={data.artistBio}
              target="_blank"
              rel="noopener noreferrer"
              className="
                mt-5
                inline-flex
                items-center
                text-xs
                font-medium
                text-white/45
                underline
                decoration-white/15
                underline-offset-4
                transition-colors
                hover:text-white
              "
            >
              Conocer al artista
            </a>
          )}
        </div>

        {/* =================================================
            ACTIONS
        ================================================== */}

        <div
          className="
            mt-8
            flex
            items-center
            justify-between
            gap-4
            landscape:mt-10
          "
        >
          {/* SOCIALS */}

          <div className="flex items-center gap-1.5">
            {data.instagramUrl && (
              <SocialButton
                href={data.instagramUrl}
                label="Instagram"
              >
                <Search
                  size={16}
                  strokeWidth={1.7}
                />
              </SocialButton>
            )}

            {data.twitterUrl && (
              <SocialButton
                href={data.twitterUrl}
                label="X"
              >
                <Heart
                  size={16}
                  strokeWidth={1.7}
                />
              </SocialButton>
            )}

            {data.youtubeUrl && (
              <SocialButton
                href={data.youtubeUrl}
                label="YouTube"
              >
                <Eye
                  size={16}
                  strokeWidth={1.7}
                />
              </SocialButton>
            )}
          </div>

          {/* PRIMARY ACTION */}

          {data.spotifyUrl && (
            <a
              href={data.spotifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Abrir en Spotify"
              className="
                flex
                h-10
                items-center
                gap-2
                rounded-full
                bg-white
                px-4
                text-xs
                font-semibold
                text-black
                transition-colors
                hover:bg-white/90
              "
            >
              <Music2
                size={15}
                strokeWidth={2}
              />

              <span>Escuchar</span>
            </a>
          )}
        </div>
      </div>
    </div>
  </div>
</footer>


)
}

function SocialButton({
href,
label,
children,
}: {
href: string
label: string
children: React.ReactNode
}) {
return ( <a
   href={href}
   target="_blank"
   rel="noopener noreferrer"
   aria-label={label}
   className="
     flex
     h-9
     w-9
     items-center
     justify-center
     rounded-full
     text-white/40
     transition-colors
     hover:bg-white/[0.07]
     hover:text-white
   "
 >
{children} </a>
)
}
